import { useState } from 'react';
import { AppError } from '@st/contract';
import type { DiscountCodeRow } from '../../api/types';
import {
  useAdminProducts,
  useCreateDiscountCode,
  useDiscountCodes,
  useSetDiscountActive,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDate, formatMoney } from '../../lib/format';
import {
  Badge,
  Button,
  DataTable,
  Input,
  Modal,
  NumberInput,
  Select,
  SkeletonList,
  useToast,
  type Column,
} from '../../ui';

interface FormState {
  code: string;
  kind: 'percent' | 'amount';
  value: number | '';
  maxRedemptions: number | '';
  validFrom: string;
  validUntil: string;
  productId: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  kind: 'percent',
  value: '',
  maxRedemptions: '',
  validFrom: '',
  validUntil: '',
  productId: '',
};

/**
 * Popust kodovi.
 *
 * Kod se ne briše, nego gasi — iskorišteni kodovi moraju ostati u povijesti da
 * bi se naplata mogla objasniti. Server odbija postotak > 100 s 422 i imenom
 * polja (`value`), pa se poruka vraća točno pod to polje, ne u toast.
 */
export function AdminDiscountCodes() {
  const { t, locale } = useTranslation();
  const query = useDiscountCodes();
  const products = useAdminProducts();
  const create = useCreateDiscountCode();
  const setActive = useSetDiscountActive();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function patch(next: Partial<FormState>) {
    setForm((f) => ({ ...f, ...next }));
  }

  async function submit() {
    setFieldErrors({});
    try {
      await create.mutateAsync({
        code: form.code.trim(),
        kind: form.kind,
        // Postotak ide kakav jest; iznos se upisuje u eurima, a server ga traži u centima.
        value:
          form.value === ''
            ? 0
            : form.kind === 'amount'
              ? Math.round(form.value * 100)
              : form.value,
        maxRedemptions: form.maxRedemptions === '' ? null : form.maxRedemptions,
        validFrom: form.validFrom ? new Date(`${form.validFrom}T00:00:00Z`).toISOString() : null,
        validUntil: form.validUntil ? new Date(`${form.validUntil}T23:59:59Z`).toISOString() : null,
        productId: form.productId ? form.productId : null,
      });
      setOpen(false);
      setForm(EMPTY_FORM);
      toast(t('discountAdmin.created'), 'success');
    } catch (err) {
      // 422 nosi imena polja; greška ostaje uz polje koje ju je izazvalo.
      if (err instanceof AppError && err.fields) {
        setFieldErrors(err.fields);
        return;
      }
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function toggle(row: DiscountCodeRow, active: boolean) {
    try {
      await setActive.mutateAsync({ id: row.id, active });
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  const columns: readonly Column<DiscountCodeRow>[] = [
    {
      key: 'code',
      header: t('discountAdmin.code'),
      render: (r) => <span className="font-medium tracking-[1px] text-ink">{r.code}</span>,
    },
    {
      key: 'value',
      header: t('discountAdmin.value'),
      render: (r) =>
        r.kind === 'percent' ? `${r.value} %` : formatMoney(r.value, 'EUR', locale),
    },
    {
      key: 'redemptions',
      header: t('discountAdmin.redemptions'),
      align: 'right',
      render: (r) =>
        r.maxRedemptions === null
          ? t('discountAdmin.unlimitedShort', { n: r.timesRedeemed })
          : `${r.timesRedeemed}/${r.maxRedemptions}`,
    },
    {
      key: 'validUntil',
      header: t('discountAdmin.validUntil'),
      hideOnMobile: true,
      render: (r) => (r.validUntil ? formatDate(r.validUntil, locale) : t('discountAdmin.noExpiry')),
    },
    {
      key: 'status',
      header: t('admin.active'),
      align: 'right',
      render: (r) => (
        <span className="flex items-center justify-end gap-3">
          <Badge tone={r.active ? 'success' : 'neutral'}>
            {r.active ? t('admin.active') : t('admin.inactive')}
          </Badge>
          {/* Kod se gasi, ne briše — iskorišteni popusti moraju ostati u povijesti. */}
          <Button variant="ghost" size="sm" onClick={() => void toggle(r, !r.active)}>
            {r.active ? t('discountAdmin.turnOff') : t('discountAdmin.turnOn')}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow={t('shell.admin')}
        title={t('discountAdmin.title')}
        action={
          <Button size="sm" onClick={() => setOpen(true)}>
            {t('discountAdmin.create')}
          </Button>
        }
      />

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('discountAdmin.empty'),
          actionLabel: t('discountAdmin.create'),
          onAction: () => setOpen(true),
        }}
      >
        {(data) => (
          <DataTable
            caption={t('discountAdmin.title')}
            rows={data.items}
            rowKey={(r) => r.id}
            columns={columns}
          />
        )}
      </QueryBoundary>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('discountAdmin.create')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={create.isPending} onClick={() => void submit()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            id="dc-code"
            label={t('discountAdmin.code')}
            hint={t('discountAdmin.codeHint')}
            value={form.code}
            autoComplete="off"
            onChange={(e) => patch({ code: e.target.value })}
            error={fieldErrors.code}
          />
          <Select
            id="dc-kind"
            label={t('discountAdmin.kind')}
            value={form.kind}
            onChange={(e) => patch({ kind: e.target.value as FormState['kind'] })}
            options={[
              { value: 'percent', label: t('discountAdmin.kindPercent') },
              { value: 'amount', label: t('discountAdmin.kindAmount') },
            ]}
          />
          <NumberInput
            id="dc-value"
            label={form.kind === 'percent' ? t('discountAdmin.percent') : t('discountAdmin.amount')}
            suffix={form.kind === 'percent' ? '%' : '€'}
            step={form.kind === 'percent' ? 1 : 0.5}
            value={form.value}
            onValueChange={(v) => patch({ value: v })}
            error={fieldErrors.value}
          />
          <NumberInput
            id="dc-max"
            label={t('discountAdmin.maxRedemptions')}
            hint={t('discountAdmin.maxRedemptionsHint')}
            step={1}
            value={form.maxRedemptions}
            onValueChange={(v) => patch({ maxRedemptions: v })}
            error={fieldErrors.maxRedemptions}
          />
          <Input
            id="dc-from"
            type="date"
            label={t('discountAdmin.validFrom')}
            value={form.validFrom}
            onChange={(e) => patch({ validFrom: e.target.value })}
            error={fieldErrors.validFrom}
          />
          <Input
            id="dc-until"
            type="date"
            label={t('discountAdmin.validUntil')}
            value={form.validUntil}
            onChange={(e) => patch({ validUntil: e.target.value })}
            error={fieldErrors.validUntil}
          />
          <Select
            id="dc-product"
            label={t('discountAdmin.product')}
            hint={t('discountAdmin.productHint')}
            value={form.productId}
            onChange={(e) => patch({ productId: e.target.value })}
            options={[
              { value: '', label: t('discountAdmin.allProducts') },
              ...(products.data?.items ?? []).map((p) => ({ value: p.id, label: p.nameHr })),
            ]}
            error={fieldErrors.productId}
          />
        </div>
      </Modal>
    </>
  );
}
